import { useMemo } from 'react'
import { match } from 'ts-pattern'
import type { ToastVariant } from '../services/toast-service.tsx'
import CloseIcon from './icons/close-icon.svg?react'
import { Spinner } from './spinner.tsx'
import * as styles from './toast.css.ts'

type ToastProps = React.PropsWithChildren<{
  variant: ToastVariant
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => unknown
  /**
   * Custom emoji icon that will overwrite the default emoji.
   * Provide a string representing the emoji, e.g., "😊".
   * When this prop is provided, it replaces the default icon set by the 'variant' prop.
   */
  emojiIcon?: string
  /**
   * Optionally adds a 'Learn More'
   */
  linkHref?: string
}>

export const Toast = ({ children, variant, onClose, emojiIcon, linkHref }: ToastProps) => {
  const icon = useIcon({ emojiIcon, variant })

  return (
    <div
      className={styles.toast({ variant })}
      role="alert"
      aria-label={`${variant} toast`}
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className={styles.toastBody}>
        <span>{icon}</span>
        <div className={styles.toastContent}>{children}</div>
        {linkHref && (
          <a href={linkHref} target="_blank" className={styles.link({ variant })}>
            Learn more
          </a>
        )}
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close">
            <CloseIcon className={styles.closeIcon({ variant })} />
          </button>
        )}
      </div>
    </div>
  )
}

type ToastMessageWithTitleProps = {
  title?: string
  detail: React.ReactNode
}

export const ToastMessageWithTitle: React.FC<ToastMessageWithTitleProps> = ({ title, detail }) =>
  title && title !== detail ? (
    <div className={styles.toastMessageWithTitle}>
      <span className={styles.toastTitle}>{title}</span>
      <span>{detail}</span>
    </div>
  ) : (
    <>{detail}</>
  )

const useIcon = ({ emojiIcon, variant }: Pick<ToastProps, 'emojiIcon' | 'variant'>) => {
  return useMemo(
    () =>
      emojiIcon ??
      match(variant)
        .with('success', () => '✔')
        .with('warning', () => '‼️️')
        .with('error', () => '🆇')
        .with('info', () => '💡')
        .with('loading', () => <Spinner size="16" label="Loading" />)
        .exhaustive(),
    [emojiIcon, variant]
  )
}
